/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable no-labels */
import React, { useEffect, useMemo, useState } from "react";
import WithAuth from "../hoc/WithAuth";
import { useGetStatusCount } from "../hooks/useGetStatusCount";
import { getStatusColor } from "../util/statusColor";
import { Button, Input, message, TableProps } from "antd";
import { useGetPayments, useMakePayment } from "../api/redemption";
import { AuthContextValue, useAuth } from "../providers/AuthProvider";
import { FilterTabs } from "../components/ui/FilterTabs";
import { AppButton } from "../components/ui/AppButton";
import { Icons } from "../assets/images/icons";
import { DataTable } from "../components/ui/data-table";
import { Voucher } from "../types/voucher";
import { useNavigate } from "react-router-dom";
import { useGetOrderStatusCount } from "../api/order";
import { formatNumber } from "../util/price-formatter";
import { formatDateToISO } from "../util/date-formatter";
import {
  getStatusText,
  getVoucherStatusConfig,
} from "../util/status-formatter";
import { useTablePrint } from "../hooks/useTablePrint";
import { ExportButton } from "../components/ui/ExportButton";

type TableRowSelection<T extends object = object> =
  TableProps<T>["rowSelection"];

function RedemptionsPage() {
  const { user } = useAuth() as AuthContextValue;
  const navigate = useNavigate();

  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;
  const [countAll, setCountAll] = useState(0);

  /** API */
  const { data: payments, isLoading: isPaymentListLoading } = useGetPayments({
    status: activeFilter === "all" ? undefined : activeFilter.toUpperCase(),
    search: searchText || undefined,
    page: currentPage - 1,
    limit: pageSize,
  });
  const { handlePrint } = useTablePrint();

  // TODO: replace it with correct api
  const { data: statuses } = useGetOrderStatusCount();

  /** Transform payments for table */
  const transformedPayments = useMemo((): Voucher[] => {
    if (!payments?.data) return [];
    return payments.data.map((item: Voucher) => ({
      id: item.id,
      voucherCode: item.voucherCode,
      voucherPrice: formatNumber(item.inputPayed) || "N/A",
      issuedDate: item.issuanceDate,
      issuedBy: item.issuedBy.fullName,
      expiryDate: item.expiryDate,
      status: item.voucherStatus,
    }));
  }, [payments]);

  /** Filter counts */
  const filterCounts = useMemo(() => {
    const statusData = statuses?.data?.data;
    return {
      all: payments?.totalElements || 0,
      accepted: statusData?.accepted || 0,
      pending: statusData?.pending || 0,
      rejected: statusData?.rejected || 0,
    };
  }, [statuses, payments]);

  const filterTabs = [
    { value: "all", label: "All", count: countAll },
    { value: "redeemed", label: "Redeemed", count: filterCounts.accepted },
  ];

  /** Handlers */
  const handleRowClick = (voucher: Voucher) =>
    navigate(`/voucher/${voucher.id}`);

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (activeFilter === "all") setCountAll(payments?.totalElements || 0);
  }, [activeFilter, payments?.totalElements]);

  /** Columns */
  const paymentColumns = [
    {
      key: "voucherCode",
      title: "Voucher Code",
      dataIndex: "voucherCode",
      align: "left" as const,
    },
    {
      key: "voucherPrice",
      title: "Voucher Price (ETB)",
      dataIndex: "voucherPrice",
      align: "right" as const,
    },
    {
      key: "issuedDate",
      title: "Issued Date",
      dataIndex: "issuedDate",
      align: "left" as const,
      render: (v: string) => formatDateToISO(v),
    },
    {
      key: "issuedBy",
      title: "Issued By",
      dataIndex: "issuedBy",
      align: "left" as const,
    },
    {
      key: "expiryDate",
      title: "Expiry Date",
      dataIndex: "expiryDate",
      align: "left" as const,
      render: (v: string) => formatDateToISO(v),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      align: "left" as const,
      render: (value: number) => (
        <div
          className={`
              inline-flex items-center justify-center min-w-[100px] max-w-[140px] px-3 py-1.5 rounded-full text-xs font-normal transition-all duration-200
              ${getVoucherStatusConfig(value).className}
            `}
        >
          {getStatusText(value)}
        </div>
      ),
    },
  ];

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [defaultIndex, setDefaultIndex] = useState(0);

  const { mutateAsync: makePayment, isPending: makingPayment } =
    useMakePayment();

  const [tableData, setTableData] = useState<
    {
      redemptionId: number;
      agroDealerName: string;
      agroDealerPhoneNo: string;
      redemedBy: string;
      redemerPhoneNo: string;
      redemptionDate: string;
      farmerName: string;
      inputName: string;
      redemptionCode: string;
      totalPrice: number;
      txnNo: string;
      paymentStatus: string;
      bankName: string;
      bankId: string;
      bankAccount: string;
    }[]
  >([]);

  const [selectedFilterIndex, setSelectedFilterIndex] = useState(0);
  const [transactionNumbers, setTransactionNumbers] = useState<
    {
      redemptionId: number;
      txnNo: string;
      redemptionCode: string;
      saveStatus: boolean;
    }[]
  >([]);

  useEffect(() => {
    if (payments && payments.data && payments.data.data) {
      const modifiedData = payments.data.data.map((item: any) => ({
        ...item,
      }));

      const paid = modifiedData.filter(
        (item: any) => item.paymentStatus === "PAYED"
      ).length;
      const pending = modifiedData.length - paid;

      let filteredData = modifiedData;

      if (searchText) {
        filteredData = modifiedData.filter(
          (item: any) =>
            item.agroDealerName
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            item.farmerName?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.redemedBy?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.txnNo?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.paymentStatus?.toLowerCase().includes(searchText.toLowerCase())
        );
      }

      if (selectedFilterIndex !== 0) {
        const selectedStatus = filters[selectedFilterIndex].label;
        filteredData = filteredData.filter(
          (item: any) => item.paymentStatus === selectedStatus
        );
      }

      setTableData(filteredData);
    }
  }, [payments, searchText, selectedFilterIndex]);

  const filters = useGetStatusCount(statuses);

  const adVoucherColumns = [
    {
      dataIndex: "farmerName",
      title: "Farmer",
    },
    {
      dataIndex: "inputName",
      title: "Input",
      render: (input: string, record: any) => (
        <>{`${input}-${record.redemptionCode}`}</>
      ),
    },
    {
      dataIndex: "totalPrice",
      title: "Total Price",
    },
    {
      dataIndex: "txnNo",
      title: "Transaction.",
      render: (transactionNo: string, record: any) => {
        if (record.paymentStatus === "PAYED") {
          return <>{transactionNo}</>;
        } else {
          return <>N/A</>;
        }
      },
    },
    {
      dataIndex: "redemedBy",
      title: "Redeemed By",
      render: (fullName: string, record: any) => (
        <div className="flex flex-col items-start">
          <div>{fullName}</div>
          <div className="text-gray-400">{record.redemerPhoneNo}</div>
        </div>
      ),
    },

    {
      dataIndex: "redemptionDate",
      title: "Redemption Date",
    },
    {
      dataIndex: "paymentStatus",
      title: "Payment Status",
      render: (status: String) => {
        const _status = status === "PAYED" ? "PAID" : "PENDING";
        return (
          <div
            className={`flex justify-center items-center w-4/5 py-[6px] rounded-xl`}
            style={{
              color: getStatusColor(_status),
              backgroundColor: `${getStatusColor(_status)}44`,
            }}
          >
            {status !== "PAYED" ? "Pending" : "Paid"}
          </div>
        );
      },
    },
  ];

  const bankVoucherColumns = [
    {
      dataIndex: "agroDealerName",
      title: "Union",
    },
    {
      dataIndex: "farmerName",
      title: "Farmer",
    },
    {
      dataIndex: "inputName",
      title: "Input",
      render: (input: string, record: any) => (
        <>{`${input}-${record.redemptionCode}`}</>
      ),
    },
    {
      dataIndex: "totalPrice",
      title: "Price",
    },
    {
      dataIndex: "txnNo",
      title: "Transaction",
      render: (transactionNo: string, record: any) => {
        if (record.paymentStatus === "PAYED") {
          return <>{transactionNo}</>;
        } else {
          if (selectedRowKeys.includes(record.redemptionId)) {
            return (
              <Input
                key={`input-${record.redemptionId}`}
                style={{ height: 30 }}
                onChange={(e: any) => {
                  const transactionNumber = e.target.value;
                  const _transactionNumbers = transactionNumbers;
                  const _transactionNo = _transactionNumbers.find(
                    (item: { redemptionId: number; txnNo: string }) =>
                      item.redemptionId == record.redemptionId
                  );
                  if (!_transactionNo) {
                    _transactionNumbers.push({
                      redemptionId: record.redemptionId,
                      redemptionCode: record.redemptionCode,
                      txnNo: transactionNumber,
                      saveStatus: false,
                    });
                    setTransactionNumbers(_transactionNumbers);
                  } else {
                    const _transactionNos = _transactionNumbers.filter(
                      (item: { redemptionId: number; txnNo: string }) =>
                        item.redemptionId != record.redemptionId
                    );
                    _transactionNos.push({
                      redemptionId: record.redemptionId,
                      redemptionCode: record.redemptionCode,
                      txnNo: transactionNumber,
                      saveStatus: false,
                    });
                    setTransactionNumbers(_transactionNos);
                  }
                }}
              />
            );
          } else {
            return <>N/A</>;
          }
        }
      },
    },
    {
      dataIndex: "bankAccount",
      title: "Account",
      render: (bankAccount: any) => {
        return bankAccount ? bankAccount : "N/A";
      },
    },
    {
      dataIndex: "redemedBy",
      title: "Redeemed By",
      render: (fullName: string, record: any) => (
        <div className="flex flex-col items-start">
          <div>{fullName}</div>
          <div className="text-gray-400">{record.redemerPhoneNo}</div>
        </div>
      ),
    },

    {
      dataIndex: "redemptionDate",
      title: "Redemption Date",
    },
    {
      dataIndex: "paymentStatus",
      title: "Payment Status",
      render: (status: String) => {
        const _status = status === "PAYED" ? "PAID" : "PENDING";
        return (
          <div
            className={`flex justify-center items-center w-4/5 py-[6px] rounded-xl`}
            style={{
              color: getStatusColor(_status),
              backgroundColor: `${getStatusColor(_status)}44`,
            }}
          >
            {status !== "PAYED" ? "Pending" : "Paid"}
          </div>
        );
      },
    },
    {
      dataIndex: "redemptionId",
      title: "Action",
      render: (redemptionId: number, record: any) => (
        <Button
          // loading={makingPayment}
          disabled={record.paymentStatus === "PAYED"}
          onClick={async () => {
            const _transactionNo = transactionNumbers.find(
              (item: {
                redemptionId: number;
                txnNo: string;
                redemptionCode: string;
                saveStatus: boolean;
              }) => item.redemptionId === redemptionId
            );
            if (_transactionNo && _transactionNo.txnNo) {
              try {
                const result = await makePayment([_transactionNo]);
                if (result.status === 200) {
                  message.success("Update payment status successfully.");
                }
              } catch (error) {
                message.error("Operation failed.");
              }
            }
          }}
        >
          Update
        </Button>
      ),
    },
  ];

  useEffect(() => {
    const _data = filterPaymentByStatus(selectedFilterIndex);

    setTableData(_data);
  }, [selectedFilterIndex]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    if (newSelectedRowKeys) {
      setSelectedRowKeys(newSelectedRowKeys);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: any) => ({
      disabled: record.paymentStatus === "PAYED",
    }),
  };

  const updateManyHandler = async () => {
    try {
      const result = await makePayment(transactionNumbers);
      if (result.status === 200) {
        message.success("Update payment status successfully.");
      }
    } catch (error) {
      message.error("Operation failed.");
    }
  };

  const filterPaymentByStatus = (data: any) => {
    if (payments && payments.data && payments.data.data) {
      if (selectedFilterIndex === 0) {
        return payments.data.data;
      } else if (selectedFilterIndex === 1) {
        return payments.data.data.filter(
          (item: any) => item.paymentStatus === "PAYED"
        );
      } else if (selectedFilterIndex === 2) {
        return payments.data.data.filter(
          (item: any) => item.paymentStatus !== "PAYED"
        );
      }
    }
  };

  return (
    <>
      <div
        className="bg-transparent lg:bg-white"
        style={{ borderRadius: 8.5, marginTop: 25 }}
      >
        {/** Header Filters */}
        <div className="space-y-4 mb-6">
          <FilterTabs
            tabs={filterTabs}
            activeTab={activeFilter}
            onTabChange={handleFilterChange}
            className="w-full lg:w-auto"
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-between">
          <div className="relative w-full lg:w-64">
            <Input
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search vouchers"
              className="pl-10 rounded-2xl w-[308px] h-11"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <img src={Icons.search} alt="search" className="w-6 h-6" />
            </div>
          </div>

          <div className="flex gap-2">
            <AppButton
              customIcon={Icons.printIcon}
              variant="outline"
              onClick={() =>
                handlePrint(
                  paymentColumns,
                  transformedPayments,
                  "Payment Report"
                )
              }
              className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            >
              Print
            </AppButton>

            <ExportButton
              columns={paymentColumns}
              data={transformedPayments}
              fileName="payments"
              title="Payment Report"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: payments?.totalElements ?? 0,
              }}
              customIcon={Icons.fileExport}
              className="bg-[#DDDDDD] hover:bg-gray-300"
            />
          </div>
        </div>

        <div className="hidden md:block">
          <DataTable<Voucher>
            data={transformedPayments}
            columns={paymentColumns}
            loading={isPaymentListLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: payments?.totalElements ?? 0,
              onChange: setCurrentPage,
            }}
            onRowClick={handleRowClick}
            emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
            emptyText="No payments found"
            rowKey="id"
          />
        </div>
      </div>
    </>
  );
}

export default WithAuth(RedemptionsPage);
